import {
  FundraisingModel,
  FundraisingNotifications,
  Step,
  TABS,
} from 'src/models/FundraisingModel';
import { FundraisingProps, renderHasChanged } from 'src/components/Pages/AddFundraisingPage';
import { Store, useAppDispatch } from 'src/redux/Store';
import { MouseEvent, useEffect, useState } from 'react';
import { setError } from 'src/redux/actions/errorsActions';
import { FORM } from 'src/constants/Form';
import Button from '@material-ui/core/Button';
import { AUTOCOMPLETE_ID } from 'src/constants/Autocomplete';
import ContactSelector from 'src/components/Control/ContactSelector';
import { useSelector } from 'react-redux';
import { indexOf } from 'lodash';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import FormUploadImage from 'src/components/Control/FormControls/FormUploadImage';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import FundraisingNotificationValidator from 'src/validations/FundraisingNotificationValidator';
import { ErrorBag } from 'src/models/ErrorModel';
import Debug from 'src/lib/Debug';
import { t } from 'src/lib/language';
import Form from 'src/components/UI/Form';
import Row from 'src/components/UI/Row';
import Subtitle from 'src/components/UI/Subtitle';
import BottomRow from 'src/components/UI/BottomRow';
import FundraiserTitle from 'src/components/UI/FundRaiserTitle';
import EditButton from 'src/components/UI/EditButton';
import DeleteButton from 'src/components/Elements/DeleteButton';
import ContactModel from 'src/models/ContactModel';
import SubmenuLink from 'src/components/UI/SubmenuLink';
import FormRichTextEditor from 'src/components/Control/FormControls/FormRichTextEditor';
import { MODALS } from 'src/models/ModalModel';
import { setSelections } from 'src/redux/actions/selectionActions';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { openModal } from 'src/redux/actions/modalActions';
import Scroller from 'src/components/UI/Scroller';
import { denormalizeFundraisingSection } from 'src/lib/FundraisingHelper';
import {
  defaultFundraisingNotifications,
  FundraisingSections,
} from 'src/constants/FundraisingTypes';
import { getContactAction, getContactByIdsAction } from 'src/redux/actions/contactActions';
import { getS3URL } from 'src/lib/S3File';

enum TAB {
  setting = 'setting',
  contacts = 'contacts',
}

const FundraisingNotificationForm = (props: FundraisingProps) => {
  const dispatch = useAppDispatch();
  const {
    onChange,
    setErrors,
    setHasChanged,
    fundraisingState,
    hasChanged,
  } = props;

  const organisation = useSelector((state: Store) => state.currentOrganisation.id);
  const lang = useSelector((state: Store) => state.language.language);
  const contacts = useSelector((state: Store) => state.contacts);
  const [notificationState, setNotificationState] = useState<FundraisingNotifications>(
    fundraisingState.notifications ?? defaultFundraisingNotifications,
  );
  const [contactId, setContactId] = useState<number | null>(null);
  const [validationError, setValidationError] = useState({} as ErrorBag);
  const [tab, setTab] = useState(TAB.setting);

  const id = fundraisingState?.fundraisingInfo?.id || 0;

  const [upload, setUpload] = useState(0);

  useEffect(() => {
    if (!contactId) {
      return;
    }
    dispatch(getContactAction(contactId));
  }, [contactId]);

  useEffect(() => {
    const missingIds = notificationState.contacts?.filter(
      (currentId) => !contacts[currentId]?.contact,
    ) || [];
    if (missingIds.length > 0) {
      dispatch(getContactByIdsAction(missingIds)); // Your new batch action
    }
  }, [notificationState.contacts, contacts]);

  useEffect(() => {
    dispatch(setError({ [FORM.fundraisingNotifications]: {} }));
  }, []);

  const onValueChange = (value: any, field: any) => {
    setHasChanged(true);
    switch (field) {
      // eslint-disable-next-line no-lone-blocks
      case 'thanks_email_body': {
        if (!notificationState.send_thanks_email) {
          return;
        }
      }
        break;
      case 'thanks_sms_text':
        if (value.length > 140) { return; }
        break;
      case 'fb_title':
        if (value.length > 75) { return; }
        break;
      case 'fb_description':
        if (value.length > 250) { return; }
        break;
      default:
    }
    setNotificationState({
      ...notificationState,
      [field]: value,
    } as FundraisingNotifications);
  };

  const onAddContact = () => {
    if (!contactId) return;
    onValueChange([
      ...notificationState.contacts,
      contactId,
    ], 'contacts');
    setContactId(null);
  };

  const handleRemoveContact = (value:number) => {
    const index = indexOf(notificationState.contacts, value);
    notificationState.contacts.splice(index, 1);
    onValueChange([...notificationState.contacts], 'contacts');
  };

  const handleEditContact = (value:number) => {
    dispatch(setSelections({
      entity: ENTITIES.contacts,
      selections: [value],
    }));
    dispatch(openModal({ modal: MODALS.editContacts }));
  };

  const getDisplayName = (contact: ContactModel) => {
    if (contact.company_name) {
      return contact.company_name;
    }
    return `${contact.first_name} ${contact.last_name}`;
  };

  const renderContact = (contact: ContactModel) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: '36px',
        gap: '8px',
        border: '1px solid rgba(0, 0, 0, 0.25)',
        padding: '8px',
        borderRadius: '8px',
      }}
      key={`ambassador-${contact.id}`}>
      <span style={{ width: '100%', textOverflow: 'ellipsis' }}>
        {getDisplayName(contact)}
      </span>
      <span style={{ marginLeft: 'auto', marginRight: '0px', width: '109px' }}>
        <EditButton onClick={() => handleEditContact(Number(contact.id))} title="Edit ambassador" />
        <span style={{ width: '5px' }}> </span>
        <DeleteButton onClick={() => handleRemoveContact(Number(contact.id))} title="Delete ambassador" />
      </span>
    </div>
  );

  const renderContacts = () => {
    if (!notificationState.contacts) return <></>;

    return notificationState.contacts.map((contact_id: number) => {
      const contactEntry = contacts[contact_id];
      if (!contactEntry?.contact) {
        return <div key={contact_id}>Loading...</div>;
      }
      return renderContact(contactEntry.contact);
    });
  };

  const renderMaxWord = (field:string, max: number) => {
    let wordCount = 0;
    if (notificationState[field]) {
      wordCount = notificationState[field].length;
    }
    return (
      <div style={{
        fontSize: '12px',
        marginTop: '-8px',
        marginBottom: '16px',
        color: '#333',
        marginLeft: '0px',
      }}>
        {`${wordCount}/${max} characters`}
      </div>
    );
  };

  const renderError = (label: string, field: string) => {
    if (validationError[field]?.length) {
      return (
        <div style={{ color: 'red' }}>
          {label} <i>{validationError[field].join(',')}</i>
        </div>
      );
    }
    return (<>{label}</>);
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>, advance: boolean) => {
    event.preventDefault();
    setErrors(TABS.notifications, false);
    const errorBag = {} as ErrorBag;
    dispatch(setError({ [FORM.fundraisingNotifications]: {} }));
    try {
      FundraisingNotificationValidator().validateSync(notificationState, { abortEarly: false });
    } catch (validationErrors: any) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'forms.group.validation_errors',
      }));
      if (!validationErrors.inner) {
        Debug.log(validationErrors);
        return;
      }
      validationErrors.inner.forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
      // eslint-disable-next-line prefer-destructuring
      dispatch(setError({ [FORM.fundraisingNotifications]: errorBag }));
      setValidationError(errorBag);
      setErrors(TABS.notifications, true);
      return;
    }
    onChange(
      {
        ...fundraisingState,
        notifications: denormalizeFundraisingSection(
          FundraisingSections.notifications, notificationState,
        ),
      } as FundraisingModel,
      Step.notifications,
      advance,
    );
  };

  const getUrl = () => `url(${getS3URL()}${organisation}/images/media_image_preview_${id}.png?${upload})`;

  return (
    <div style={{ height: '688px' }}>
      <Form maxWidth="100%">
        <Row height="auto">
          <FundraiserTitle>Notifications/Social</FundraiserTitle>
        </Row>
        <Row>
          <SubmenuLink
            color="rgb(165, 23, 96)"
            onClick={() => setTab(TAB.setting)}
            selected={tab === TAB.setting}>
            Settings
          </SubmenuLink>
          <SubmenuLink
            color="rgb(165, 23, 96)"
            onClick={() => setTab(TAB.contacts)}
            selected={tab === TAB.contacts}>
            Contacts
          </SubmenuLink>
        </Row>
        { tab === TAB.contacts && (
          <>
            <Subtitle style={{ marginTop: '0px' }}>
              { renderError(t(lang, 'forms.fundraising.contacts_to_notify'), 'contacts') }
            </Subtitle>
            <Row height="auto">
              <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
                <FormCheckbox
                  label="Send email notifications"
                  name="send_email_notifications"
                  onChange={onValueChange}
                  checked={!!notificationState.send_email_notifications} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
                <FormCheckbox
                  label="Send SMS notifications"
                  name="send_sms_notifications"
                  onChange={onValueChange}
                  checked={!!notificationState.send_sms_notifications} />
              </div>
            </Row>
            <Row height="auto">
              <ContactSelector
                autocompleteId={AUTOCOMPLETE_ID.add_fundraising_contact}
                form={FORM.fundraisingNotifications}
                name="add_contact"
                label={t(lang, 'forms.fundraising.add_contact')}
                onChange={(value: number) => setContactId(value)}
                margin="dense"
                contactId={contactId || undefined} />
              <Button
                style={{ marginBottom: '16px', height: '41px' }}
                variant="contained"
                color="primary"
                onClick={onAddContact}
                key="add-contact-button"
                disabled={!contactId}>
                Add
              </Button>
            </Row>
            <Row height="auto">
              <Scroller height="410px" style={{ width: '100%' }}>
                <div style={{
                  width: '100%', display: 'flex', flexDirection: 'column', gap: '8px',
                }}>
                  {renderContacts()}
                </div>
              </Scroller>
            </Row>
          </>
        )}
        { tab === TAB.setting && (
          <Scroller height="565px">
            <Row alignItems="start">
              <div style={{ width: '50%' }}>
                <Subtitle style={{ marginTop: '0px' }}>
                  Social media texts and image
                </Subtitle>
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Row>
                    <FormTextField
                      style={{ marginTop: '8px' }}
                      label="Message description (max 250 chars)"
                      form={FORM.fundraisingNotifications}
                      name="fb_description"
                      onChange={onValueChange}
                      value={notificationState.fb_description}
                      minRows={3}
                      maxRows={3}
                      multiline />
                  </Row>
                  {renderMaxWord('fb_description', 250)}
                  <Row>
                    <FormTextField
                      style={{ marginTop: '0px' }}
                      form={FORM.fundraisingNotifications}
                      label="Title (max 75 chars)"
                      name="fb_title"
                      onChange={onValueChange}
                      value={notificationState.fb_title}
                      margin="dense" />
                  </Row>
                  {renderMaxWord('fb_title', 75)}
                  <Row alignItems="self-start" style={{ flexDirection: 'column', gap: '0px' }}>
                    <div style={{
                      width: '300px',
                      height: '200px',
                      backgroundImage: getUrl(),
                      backgroundColor: 'grey',
                    }}>
                    &nbsp;
                    </div>
                    <FormUploadImage
                      name={`media_image_preview_${id}.png`}
                      onUpload={() => setUpload(upload + 1)}
                      height={200}
                      width={300}
                      hidePreview
                      path={`${organisation}/images/media_image_preview_${id}.png`} />
                  </Row>
                </div>
              </div>
              <div style={{ width: '50%' }}>
                <Subtitle style={{ marginTop: '0px' }}>
                  Email thank you message
                </Subtitle>
                <FormCheckbox
                  label="Send Email thank you message"
                  name="send_thanks_email"
                  onChange={onValueChange}
                  checked={!!notificationState.send_thanks_email} />
                { !!notificationState.send_thanks_email && (
                  <>
                    <FormRichTextEditor
                      height="200px"
                      label="Thanks email body"
                      form={FORM.fundraisingNotifications}
                      name="thanks_email_body"
                      onChange={onValueChange}
                      value={notificationState.thanks_email_body || ''}
                      disabled={!notificationState.send_thanks_email} />
                  </>
                )}
                <Subtitle style={{ marginTop: '8px' }}>
                  SMS thank you message
                </Subtitle>
                <FormCheckbox
                  style={{ marginTop: '0px', marginBottom: '-16px' }}
                  label="Send SMS thank you message"
                  name="send_thanks_sms"
                  onChange={onValueChange}
                  checked={!!notificationState.send_thanks_sms} />
                { !!notificationState.send_thanks_sms && (
                  <>
                    <Row>
                      <FormTextField
                        style={{ marginTop: '8px' }}
                        label="SMS thank you message (max 140 chars)"
                        form={FORM.fundraisingNotifications}
                        name="thanks_sms_text"
                        onChange={onValueChange}
                        value={notificationState.thanks_sms_text}
                        disabled={!notificationState.send_thanks_sms}
                        minRows={3}
                        maxRows={3}
                        multiline />
                    </Row>
                    {renderMaxWord('thanks_sms_text', 140)}
                  </>
                )}
              </div>
            </Row>
          </Scroller>
        )}
        <BottomRow>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => handleSubmit(e, false)}
            key="submit-button">
            Save{renderHasChanged(!hasChanged)}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => handleSubmit(e, true)}
            key="submit-button-next">
            Save{renderHasChanged(!hasChanged)} and Next Step
          </Button>
        </BottomRow>
      </Form>
    </div>
  );
};

export default FundraisingNotificationForm;
